<template>
  <div
    v-if="activeItem"
    class="pixso-tabbed-video"
    :class="{ 'pixso-tabbed-video--en': isEnglish }"
    data-text-reveal="off"
  >
    <figure class="pixso-tabbed-video__surface">
      <video
        v-for="layer in videoLayers"
        :key="layer.videoKey"
        class="pixso-tabbed-video__media"
        :class="{ 'pixso-tabbed-video__media--active': layer.isActive }"
        :poster="poster || undefined"
        autoplay
        muted
        loop
        playsinline
        preload="auto"
        :aria-label="layer.item.ariaLabel || layer.item.buttonLabel"
        @loadeddata="handleLayerReady(layer.name, layer.itemKey, $event)"
        @canplay="handleLayerReady(layer.name, layer.itemKey, $event)"
      >
        <source :src="layer.item.src" type="video/mp4" />
      </video>
      <figcaption v-if="caption">{{ caption }}</figcaption>
    </figure>

    <div class="pixso-tabbed-video__tabs" role="tablist" :aria-label="tablistLabel">
      <div
        v-for="(row, rowIndex) in itemRows"
        :key="`tabbed-video-row-${rowIndex}`"
        class="pixso-tabbed-video__row"
      >
        <button
          v-for="item in row"
          :key="item.key"
          class="pixso-tabbed-video__chip"
          :class="{ 'pixso-tabbed-video__chip--active': selectedIndex === item.itemIndex }"
          type="button"
          role="tab"
          :aria-selected="selectedIndex === item.itemIndex"
          @click="selectItem(item.itemIndex)"
        >
          {{ item.buttonLabel }}
        </button>
      </div>
    </div>

    <p v-if="activeItem.subtitle" class="pixso-tabbed-video__subtitle pixso-body-md">
      {{ activeItem.subtitle }}
    </p>
  </div>
</template>

<script lang="ts">
export default {
  inheritAttrs: false
}
</script>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

export interface TabbedVideoPlayerItem {
  key: string
  src: string
  buttonLabel: string
  subtitle?: string
  ariaLabel?: string
}

const props = withDefaults(defineProps<{
  items: TabbedVideoPlayerItem[]
  poster?: string
  caption?: string
  tablistLabel?: string
  initialIndex?: number
}>(), {
  poster: '',
  caption: '',
  tablistLabel: '',
  initialIndex: 0
})

const emit = defineEmits<{
  (event: 'change', payload: { item: TabbedVideoPlayerItem; index: number }): void
}>()

const DESKTOP_ROW_COUNT = 5
const COMPACT_ROW_COUNT = 4
const MOBILE_ROW_COUNT = 3
const MOBILE_ROW_COUNT_EN = 2
const MIN_ITEMS_PER_ROW = 2

const { locale } = useI18n()
const isEnglish = computed(() => locale.value === 'en')
const selectedIndex = ref(0)
const primaryIndex = ref<number | null>(0)
const secondaryIndex = ref<number | null>(null)
const activeLayer = ref<'primary' | 'secondary'>('primary')
const loadingLayer = ref<'primary' | 'secondary' | null>(null)
const loadingItemKey = ref('')
const viewportMode = ref<'desktop' | 'compact' | 'mobile'>('desktop')

let compactQuery: MediaQueryList | null = null
let mobileQuery: MediaQueryList | null = null

const clampIndex = (value: number) => {
  if (!props.items.length) {
    return 0
  }

  const index = Number.isFinite(value) ? Math.trunc(value) : 0
  return Math.min(Math.max(index, 0), props.items.length - 1)
}

const indexedItems = computed(() => (
  props.items.map((item, itemIndex) => ({
    ...item,
    itemIndex
  }))
))

const rebalanceOrphanRows = <T,>(rows: T[][]) => {
  if (MIN_ITEMS_PER_ROW <= 1) {
    return rows
  }

  const balancedRows = rows.map((row) => [...row])

  for (let index = balancedRows.length - 1; index > 0; index -= 1) {
    if (balancedRows[index].length !== 1) {
      continue
    }

    const previousRow = balancedRows[index - 1]

    if (previousRow.length > MIN_ITEMS_PER_ROW) {
      balancedRows[index].unshift(previousRow.pop() as T)
    } else {
      previousRow.push(...balancedRows[index])
      balancedRows.splice(index, 1)
    }
  }

  return balancedRows
}

const chunkItems = <T,>(items: T[], preferredRowCount: number) => {
  const rowCount = Math.max(Math.trunc(preferredRowCount), MIN_ITEMS_PER_ROW)

  if (items.length <= rowCount) {
    return [items]
  }

  const rows: T[][] = []
  for (let index = 0; index < items.length; index += rowCount) {
    rows.push(items.slice(index, index + rowCount))
  }

  return rebalanceOrphanRows(rows)
}

const preferredRowCount = computed(() => {
  if (viewportMode.value === 'mobile') {
    return isEnglish.value ? MOBILE_ROW_COUNT_EN : MOBILE_ROW_COUNT
  }

  if (viewportMode.value === 'compact') {
    return COMPACT_ROW_COUNT
  }

  return DESKTOP_ROW_COUNT
})

const itemRows = computed(() => {
  return chunkItems(indexedItems.value, preferredRowCount.value).filter((row) => row.length)
})

const getItemKey = (item: TabbedVideoPlayerItem) => `${item.key}-${item.src}`

const getLayerIndex = (layer: 'primary' | 'secondary') => {
  return layer === 'primary' ? primaryIndex.value : secondaryIndex.value
}

const setLayerIndex = (layer: 'primary' | 'secondary', index: number | null) => {
  if (layer === 'primary') {
    primaryIndex.value = index
    return
  }

  secondaryIndex.value = index
}

const inactiveLayer = computed<'primary' | 'secondary'>(() => (
  activeLayer.value === 'primary' ? 'secondary' : 'primary'
))

const activeItem = computed(() => {
  const index = getLayerIndex(activeLayer.value)
  return typeof index === 'number' ? props.items[clampIndex(index)] || null : null
})

const videoLayers = computed(() => {
  return (['primary', 'secondary'] as const)
    .map((name) => {
      const index = getLayerIndex(name)
      const item = typeof index === 'number' ? props.items[clampIndex(index)] || null : null

      if (!item) {
        return null
      }

      const itemKey = getItemKey(item)

      return {
        name,
        item,
        itemKey,
        isActive: activeLayer.value === name,
        videoKey: `${name}-${itemKey}`
      }
    })
    .filter((layer): layer is NonNullable<typeof layer> => Boolean(layer))
})

const selectItem = (index: number) => {
  const nextIndex = clampIndex(index)
  selectedIndex.value = nextIndex

  const nextItem = props.items[nextIndex]

  if (nextItem) {
    emit('change', { item: nextItem, index: selectedIndex.value })
  }

  if (!nextItem || nextIndex === getLayerIndex(activeLayer.value)) {
    return
  }

  const targetLayer = inactiveLayer.value
  const nextItemKey = getItemKey(nextItem)

  setLayerIndex(targetLayer, nextIndex)
  loadingLayer.value = targetLayer
  loadingItemKey.value = nextItemKey
}

const handleLayerReady = (
  layer: 'primary' | 'secondary',
  itemKey: string,
  event: Event
) => {
  const video = event.currentTarget as HTMLVideoElement | null

  if (video) {
    video.play().catch(() => {
      // Autoplay is muted, but browsers can still reject while a source is changing.
    })
  }

  if (loadingLayer.value !== layer || loadingItemKey.value !== itemKey) {
    return
  }

  activeLayer.value = layer
  loadingLayer.value = null
  loadingItemKey.value = ''
}

watch(
  () => props.initialIndex,
  (value) => {
    const nextIndex = clampIndex(value)
    selectedIndex.value = nextIndex
    setLayerIndex(activeLayer.value, nextIndex)
    setLayerIndex(inactiveLayer.value, null)
    loadingLayer.value = null
    loadingItemKey.value = ''
  },
  { immediate: true }
)

watch(
  () => props.items.length,
  () => {
    const nextIndex = clampIndex(selectedIndex.value)
    selectedIndex.value = nextIndex
    setLayerIndex(activeLayer.value, nextIndex)
    setLayerIndex(inactiveLayer.value, null)
    loadingLayer.value = null
    loadingItemKey.value = ''
  }
)

const updateViewportMode = () => {
  if (mobileQuery?.matches) {
    viewportMode.value = 'mobile'
    return
  }

  if (compactQuery?.matches) {
    viewportMode.value = 'compact'
    return
  }

  viewportMode.value = 'desktop'
}

onMounted(() => {
  if (typeof window === 'undefined') {
    return
  }

  compactQuery = window.matchMedia('(max-width: 1200px)')
  mobileQuery = window.matchMedia('(max-width: 640px)')
  updateViewportMode()
  compactQuery.addEventListener('change', updateViewportMode)
  mobileQuery.addEventListener('change', updateViewportMode)
})

onBeforeUnmount(() => {
  compactQuery?.removeEventListener('change', updateViewportMode)
  mobileQuery?.removeEventListener('change', updateViewportMode)
  compactQuery = null
  mobileQuery = null
})
</script>

<style scoped>
.pixso-tabbed-video {
  margin-top: clamp(48px, 4vw, 64px);
}

.pixso-tabbed-video__surface {
  position: relative;
  max-width: 1128px;
  aspect-ratio: 16 / 9;
  margin: 0 auto;
  overflow: hidden;
  border-radius: calc(var(--pixso-radius-lg) - var(--pixso-gap-xs));
  background: var(--pixso-color-surface);
}

.pixso-tabbed-video__media {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.18s ease;
  pointer-events: none;
}

.pixso-tabbed-video__media--active {
  opacity: 1;
}

.pixso-tabbed-video__surface figcaption {
  position: absolute;
  top: var(--pixso-gap-md);
  left: var(--pixso-gap-md);
  margin: 0;
  color: var(--pixso-color-text);
  font-size: var(--pixso-fs-16);
  line-height: var(--pixso-lh-16);
}

.pixso-tabbed-video__tabs {
  margin-top: var(--pixso-gap-md);
  display: flex;
  flex-direction: column;
  gap: var(--pixso-gap-sm);
  margin-inline: auto;
}

.pixso-tabbed-video__row {
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  gap: var(--pixso-gap-sm);
}

.pixso-tabbed-video__chip {
  border: 1px solid transparent;
  border-radius: 999px;
  background: var(--pixso-color-surface);
  color: rgba(255, 255, 255, 0.72);
  padding: var(--pixso-fs-10) var(--pixso-fs-20);
  font-size: var(--pixso-fs-14);
  font-family: "Montserrat", "HarmonyOS Sans", sans-serif;
  font-weight: 700;
  line-height: var(--pixso-lh-14);
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
}

.pixso-tabbed-video__chip:not(.pixso-tabbed-video__chip--active):hover {
  color: var(--pixso-color-text);
  border-color: rgba(255, 255, 255, 0.32);
}

.pixso-tabbed-video__chip:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.78);
  outline-offset: 3px;
}

.pixso-tabbed-video__chip--active,
.pixso-tabbed-video__chip--active:hover,
.pixso-tabbed-video__chip--active:focus-visible {
  background: var(--pixso-color-text);
  color: var(--pixso-color-bg) !important;
  -webkit-text-fill-color: var(--pixso-color-bg);
}

.pixso-tabbed-video__chip--active :deep(*) {
  color: var(--pixso-color-bg) !important;
  -webkit-text-fill-color: var(--pixso-color-bg);
}

.pixso-tabbed-video__subtitle {
  margin: 20px auto 0;
  max-width: 720px;
  color: var(--pixso-color-muted);
  text-align: center;
}

@media (max-width: 1200px) {
  .pixso-tabbed-video__tabs {
    max-width: 760px;
  }

}

@media (max-width: 640px) {
  .pixso-tabbed-video__tabs {
    max-width: 100%;
  }

  .pixso-tabbed-video__row {
    gap: calc(var(--pixso-gap-sm) - var(--pixso-gap-xs));
  }

  .pixso-tabbed-video__surface figcaption {
    top: calc(var(--pixso-gap-sm) + var(--pixso-gap-xs));
    left: calc(var(--pixso-gap-sm) + var(--pixso-gap-xs));
    font-size: var(--pixso-fs-14);
    line-height: var(--pixso-lh-14);
  }

  .pixso-tabbed-video__chip {
    padding: calc(var(--pixso-gap-sm) - var(--pixso-gap-xs)) var(--pixso-fs-14);
    font-size: var(--pixso-fs-12);
    line-height: var(--pixso-lh-12);
  }

  .pixso-tabbed-video--en .pixso-tabbed-video__row {
    flex-wrap: wrap;
  }

  .pixso-tabbed-video--en .pixso-tabbed-video__chip {
    max-width: 100%;
    white-space: normal;
    text-align: center;
  }

  .pixso-tabbed-video__subtitle {
    font-size: var(--pixso-fs-14);
    line-height: var(--pixso-lh-14);
  }
}
</style>
